/**
 * The Oracle-only More ▸ DBA actions — Tablespace Manager, Package Explorer,
 * Data Pump.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11. Each passes
 * `supportsOracleDbaEngineFilter` to `requestContext`, the same predicate that
 * gates its menu row's visibility, so a visible row can never resolve a
 * non-Oracle connection (INJ-2 round 2).
 */
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useWorkspaceStore } from '../../workspace';
import {
  createDefaultPackageExplorerContent,
  findExistingPackageExplorerTab,
} from '../../workspace/package-explorer/types';
import { supportsOracleDbaEngineFilter } from '../model/toolbarEngineFilters';
import type { ToolbarActionContext } from './toolbarActionContext';

export function useToolbarOracleActions(ctx: ToolbarActionContext) {
  const { track, requestContext, addTab } = ctx;

  const handleOpenTablespaceManager = async () => {
    addBreadcrumb('click', 'toolbar:tablespace-manager');
    track('toolbar:tablespace_manager');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsOracleDbaEngineFilter },
      'Tablespace Manager'
    );
    if (!context) return;

    addTab({
      title: `Tablespaces: ${context.connectionName}`,
      type: 'tablespace-manager',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        isLoading: true,
      },
    });
  };

  const handleOpenPackageExplorer = async () => {
    addBreadcrumb('click', 'toolbar:package-explorer');
    track('toolbar:package_explorer');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsOracleDbaEngineFilter },
      'Package Explorer'
    );
    if (!context) return;

    const { tabs, setActiveTab } = useWorkspaceStore.getState();
    const existing = findExistingPackageExplorerTab(tabs, context.connectionId, context.database ?? '');
    if (existing) {
      setActiveTab(existing.id);
      return;
    }

    addTab({
      title: `Package Explorer — ${context.database ?? ''}`,
      type: 'package-explorer',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
        database: context.database,
      },
      content: createDefaultPackageExplorerContent({
        connectionId: context.connectionId,
        database: context.database ?? '',
      }),
    });
  };

  const handleOpenDataPump = async () => {
    addBreadcrumb('click', 'toolbar:data-pump');
    track('toolbar:data_pump');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsOracleDbaEngineFilter },
      'Data Pump'
    );
    if (!context) return;

    addTab({
      title: `Data Pump: ${context.connectionName}`,
      type: 'data-pump',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        mode: 'export',
        wizardStep: 'prerequisites',
        isLoading: true,
      },
    });
  };

  return {
    'tablespace-manager': () => void handleOpenTablespaceManager(),
    'package-explorer': () => void handleOpenPackageExplorer(),
    'data-pump': () => void handleOpenDataPump(),
  };
}
